import React, { useState } from "react";
import {
  Building,
  CreditCard,
  BarChart3,
  MessageSquare,
  Users,
  DollarSign,
  Bell,
  TrendingUp,
  Copy,
  Eye,
  EyeOff,
} from "lucide-react";
import { Link } from "react-router-dom";

const Dashboard: React.FC = () => {
  const [showApiKey, setShowApiKey] = useState(false);
  const apiKey = "sk_live_51H7QjKBQBNvn2XkNc...";

  const stats = [
    {
      title: "Total Credits",
      value: "45,280",
      change: "+12%",
      icon: DollarSign,
      color: "text-green-600",
    },
    {
      title: "Messages Sent",
      value: "128,456",
      change: "+8%",
      icon: MessageSquare,
      color: "text-blue-600",
    },
    {
      title: "Active Customers",
      value: "89",
      change: "+15%",
      icon: Users,
      color: "text-purple-600",
    },
    {
      title: "Commission Earned",
      value: "₦2,847",
      change: "+22%",
      icon: TrendingUp,
      color: "text-orange-600",
    },
  ];

  const recentActivity = [
    {
      id: 1,
      customer: "TechCorp Ltd",
      action: "Purchased 10,000 credits",
      amount: "₦850",
      time: "2 hours ago",
    },
    {
      id: 2,
      customer: "Marketing Pro",
      action: "Sent 2,500 messages",
      amount: "-₦125",
      time: "4 hours ago",
    },
    {
      id: 3,
      customer: "E-commerce Hub",
      action: "Purchased 5,000 credits",
      amount: "₦425",
      time: "6 hours ago",
    },
    {
      id: 4,
      customer: "StartupXYZ",
      action: "Registered new account",
      amount: "₦0",
      time: "8 hours ago",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                SMS Hub Reseller Dashboard
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <Bell size={20} />
              </button>
              <Link
                to={"/campaign"}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Campaigns
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="p-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => (
            <div
              key={stat.title}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {stat.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stat.value}
                  </p>
                  <p className={`text-sm ${stat.color} font-medium`}>
                    {stat.change} from last month
                  </p>
                </div>
                <div className={`p-3 rounded-lg bg-gray-50`}>
                  <stat.icon size={24} className={stat.color} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold">Recent Activity</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Building size={16} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {activity.customer}
                        </p>
                        <p className="text-sm text-gray-600">
                          {activity.action}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-medium ${
                          activity.amount.startsWith("-")
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {activity.amount}
                      </p>
                      <p className="text-sm text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            {/* API Keys */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold mb-4">API Integration</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type={showApiKey ? "text" : "password"}
                      value={apiKey}
                      readOnly
                      className="flex-1 p-3 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                    />
                    <button
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="p-3 text-gray-400 hover:text-gray-600"
                    >
                      {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button className="p-3 text-gray-400 hover:text-gray-600">
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
                <button className="w-full py-2 px-4  text-center  text-[14px] hover:underline h-[40px]  ml-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  View Documentation
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold mb-4">This Month</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Messages Sent</span>
                  <span className="font-semibold">24,567</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Success Rate</span>
                  <span className="font-semibold text-green-600">98.7%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">New Customers</span>
                  <span className="font-semibold">12</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Commission</span>
                  <span className="font-semibold text-green-600">₦847</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button className="w-full py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center">
                  <Users size={16} className="mr-2" />
                  Add New Customer
                </button>
                <button className="w-full py-3 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center">
                  <CreditCard size={16} className="mr-2" />
                  Purchase Credits
                </button>
                <button className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center">
                  <BarChart3 size={16} className="mr-2" />
                  View Reports
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
